// frontend/src/components/Header.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Header.css';

function pickUsername(user) {
  if (!user) return 'User';
  return user.username || user.email || user.name || 'User';
}

function initialsFrom(name) {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  const first = parts[0]?.[0] || 'U';
  const second = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + second).toUpperCase();
}

export default function Header({ publicOnly = false } = {}) {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const username = useMemo(() => pickUsername(user), [user]);
  const initials = useMemo(() => initialsFrom(username), [username]);

  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(e.target)) setOpen(false);
    };

    const onEsc = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onEsc);
    };
  }, []);

  const onLogout = () => {
    setOpen(false);
    logout();
    navigate('/', { replace: true });
  };

  const onLogin = () => {
    const requested = `${location.pathname}${location.search}${location.hash}`;
    navigate('/login', { state: { from: requested } });
  };

  return (
    <header className="pf-header">
      <div className="pf-header__inner">
        <div className="pf-header__left">
          <Link to="/" className="pf-header__brand" aria-label="profitfactor.io home">
            profitfactor.io
          </Link>
        </div>

        <nav className="pf-header__nav" aria-label="Primary navigation">
          <NavLink to="/screener" className={({ isActive }) => `pf-navlink ${isActive ? 'is-active' : ''}`}>
            Strategy Screener
          </NavLink>
          {!publicOnly && isAuthenticated ? (
            <>
              <NavLink to="/my/accounts" className={({ isActive }) => `pf-navlink ${isActive ? 'is-active' : ''}`}>
                My Accounts
              </NavLink>
              <NavLink
                to="/my/strategies"
                className={({ isActive }) => `pf-navlink ${isActive ? 'is-active' : ''}`}
              >
                My Strategies
              </NavLink>
            </>
          ) : null}
        </nav>

        <div className="pf-header__right" ref={menuRef}>
          {!publicOnly && isAuthenticated ? (
            <>
              <button
                type="button"
                className="pf-user"
                onClick={() => setOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={open}
              >
                <span className="pf-avatar" aria-hidden="true">
                  {initials}
                </span>
                <span className="pf-username" title={username}>
                  {username}
                </span>
                <span className="pf-caret" aria-hidden="true">
                  ▾
                </span>
              </button>

              {open && (
                <div className="pf-dropdown" role="menu" aria-label="User menu">
                  <NavLink
                    to="/profile"
                    className="pf-dropdown__item"
                    role="menuitem"
                    onClick={() => setOpen(false)}
                  >
                    Profile
                  </NavLink>
                  <button type="button" className="pf-dropdown__item pf-dropdown__danger" role="menuitem" onClick={onLogout}>
                    Logout
                  </button>
                </div>
              )}
            </>
          ) : !publicOnly ? (
            <button type="button" className="pf-user" onClick={onLogin}>
              <span className="pf-avatar" aria-hidden="true">
                ⇢
              </span>
              <span className="pf-username">Login</span>
            </button>
          ) : null}
        </div>
      </div>
    </header>
  );
}
